<template>
  <div
    class="draft-editor bg-white flex w-full"
    :data-has-draft="!!(draftContent || hasAttachments)"
  >
    <!-- 提示占位用 -->
    <div
      v-if="inputIndicator"
      class="
        is-inactive flex w-full px-5 py-3 border-t border-solid border-gray-200
      "
    >
      <div
        class="
          px-3 py-2 w-full rounded teld-bg-color-gray teld-color-lightgray
          van-ellipsis
        "
        @click="showInputPopup">{{
        trimmedContent || placeholder
      }}
      </div>
    </div>

    <!-- 弹出层输入框 -->
    <tm-popup
      v-model="isFocused"
      position="bottom"
      :overlay="true"
      get-container="body"
      :overlay-style="{background:'rgba(0 0 0/0.45)'}"
      round
      class="popup-for-draft-editor"
      :style="{
        height: isFullscreen ? 'calc(100% - 180px)' : '280px',
        overflow: 'hidden',
        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 8px 2px, rgba(0, 0, 0, 0.1) 0px 0 6px -2px'
      }"
    >
      <!-- 主体 -->
      <div
        ref="inputWrapperRef"
        class="
          right-main flex flex-col rounded relative
          box-border px-5 py-4 overflow-hidden h-full
        "
        :class="[isFocused ? '' : 'h-0']"
        @click="onClickInputWrapper"
      >
        <div class="flex flex-1 overflow-hidden relative pb-4">
          <!-- 输入框 -->
          <div
            ref="draftEditorInputRef"
            :contentEditable="visible"
            class="
              draft-editor--input flex-1
              mr-2 box-border
              overflow-x-hidden
              overflow-y-auto
            "
            :class="[
              isFullscreen ? 'flex-1' : ''
            ]"
            style="white-space:pre-wrap;word-break:break-word;"
            @paste="onPaste"
          ></div>
          <!-- 切换全屏大小的图标按钮 -->
          <div class="icons-toggle">
            <ToFullscreen v-show="!isFullscreen" @click.native.stop.prevent="toggleFullscreen" />
            <ToWindowed v-show="isFullscreen" @click.native.stop.prevent="toggleFullscreen" />
          </div>
          <!-- 占位提示 -->
          <div
            v-show="!this.draftContent"
            class="draft-placeholder pointer-events-none absolute teld-color-lightgray"
          >{{ placeholder }}</div>

          <!-- 已输入长度/限制长度 -->
          <div
            class="
              draft-content-counting
              absolute right-0 bottom-0
            "
            :class="[ isInputOverflow ? 'teld-color-red' : 'teld-color-lightgray']"
            style=""
          >{{draftWordLength}}/{{INPUT_WORD_LIMIT}}</div>
        </div>

        <!-- 附件 -->
        <div
          v-show="hasAttachments"
          class="
            file-list-preview py-3 pr-2
            border-b border-solid border-gray-100
            flex overflow-x-auto
          "
        >
          <div
            v-for="(fileItem, fileIndex) in fileList"
            :key="fileItem.file.name + fileItem.file.lastModified"
            class="relative h-20 mr-2 "
          >
            <tm-loading
              v-if="fileItem && fileItem.status !== 'done'"
              type="spinner"
              class="
                absolute w-full h-full flex justify-center items-center
                opacity-20 text-white bg-black
              "
            ></tm-loading>

            <img
              v-if="
                fileItem.file.type == 'image/jpeg' ||
                fileItem.file.type == 'image/png' ||
                fileItem.file.type == 'image/jpg' ||
                fileItem.file.type == 'image/gif'
              "
              :src="fileItem.content"
              alt="Image"
              class="attachment-image w-28 h-20 border border-solid border-gray-100 rounded"
              @click="onClickImage"
            />
            <div
              v-else
              style="
                background-color: #f5f5f5;
              "
              class="p-2 flex flex-col justify-between rounded w-28 h-20 box-border"
            >
              <!-- 文件类型图标 -->
              <FileTypeIcon
                :file-name="fileItem.file && fileItem.file.name"
                class="w-6 h-6 self-center"
              />
              <div class="van-multi-ellipsis--l2 text-xs">
                {{ fileItem.file.name }}
              </div>
            </div>
            <!-- 删除附件 -->
            <div
              class="file-remove-icon-container absolute -top-1 -right-1"
              @click="handleRemoveFile(fileIndex)"
            >
              <Clear class="w-5 h-5 bg-white rounded-full" style="color:#8a8a8a" />
            </div>
          </div>
        </div>

        <!-- 底部按钮行 -->
        <div
          class="
            operation-wrap flex justify-between items-center pt-2
            border-t border-solid border-gray-100
          "
        >
          <div class="left teld-theme-color pl-1">
            <At
              :tabindex="visible ? 0 : -1"
              class="teld-theme-color cursor-pointer mr-3 outline-on-focus hidden"
              @click.native.capture.stop="onClickAt"
            />
            <!-- <Attachment
              :tabindex="visible ? 0 : -1"
              class="teld-theme-color cursor-pointer outline-on-focus"
              @click.native.capture.stop="onClickAttachment"
            /> -->
            <tm-uploader
              ref="uploaderRef"
              :before-read="beforeRead"
              :after-read="afterRead"
              accept="*/*"
              multiple
              class="mr-5"
            >
              <!-- 附件图标 -->
              <div
                class="teld-theme-color cursor-pointer"
              >
                <Attachment />
              </div>
            </tm-uploader>
          </div>
          <div class="right">
            <tm-button
              type="primary"
              color="var(--theme-color)"
              size="small"
              @click.stop="onClickPublishComment"
            >{{ isComment ? '评论' : '回复'}}</tm-button>
          </div>
        </div>
      </div>
    </tm-popup>
  </div>
</template>

<script>
// Components
import ToFullscreen from "../../svg-icons/to-fullscreen.vue";
import ToWindowed from "../../svg-icons/to-windowed.vue";
import At from "../../svg-icons/at.vue";
import Attachment from "../../svg-icons/attachment.vue";
import Clear from "../../svg-icons/clear.vue";
import UserAvatar from '../../user-avatar.vue';
import FileTypeIcon from '../../file-type-icon/index.vue';

// Utils
import {
  SUPPORTED_FILE_TYPES,
  INPUT_WORD_LIMIT,
  SUBMIT_CONTENT_LIMIT,
  formatBytes,
} from '../../../core';

/**
 * [移动端用]草稿输入框组件
 */
export default {
  name: "TmDraftEditor",
  inject: ["loginUserInfo", 'getAttachmentMaxSize'],
  props: {
    isComment: {
      type: Boolean,
      default: false,
    },
    /**透传visible用于点开时给editor赋焦点*/
    visible: {
      type: Boolean,
      default: false,
    },
    /**是否可独立窗口打开*/
    canBeIndependent: {
      type: Boolean,
      default: false,
    },
    showAvatar: {
      type: Boolean,
      default: false,
    },
    autoClearOnHidding: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '评论一下...',
    },
    inputIndicator: {
      type: Boolean,
      default: false,
    }
  },
  components: {
    ToFullscreen,
    ToWindowed,
    At,
    Attachment,
    Clear,
    UserAvatar,
    FileTypeIcon,
  },
  watch: {
    visible(val) {
      if (val) {
        !this.inputIndicator && this.showInputPopup();
      } else {
        this.autoClearOnHidding && this.reset();
      }
    },
    isFocused(val) {
      if (val) {
        this.$nextTick(()=>{
          this.bindEvents();
        })
      } else {
        this.isFullscreen = false;
        if (!this.inputIndicator) {
          this.$emit('update:visible', false);
        }
        this.unbindEvents();
      }
    },
  },
  computed: {
    loginUser() {
      return this.loginUserInfo && this.loginUserInfo();
    },
    hasAttachments() {
      return !!this.fileList.length;
    },
    isInputOverflow() {
      return this.draftWordLength > INPUT_WORD_LIMIT;
    },
  },
  data() {
    this.INPUT_WORD_LIMIT = INPUT_WORD_LIMIT;
    return {
      fileList: [],
      // fileList: [
      //   {
      //     file:{
      //       name:'test-image.png',
      //       uid: '1708939944188',
      //       size:'1323423',
      //       type: 'image/png',
      //     },
      //     result: {
      //       "CommentID": null,
      //       "AttachmentType": 0,
      //       "AttachmentUrl": "http://resource.teld3.xyz/teldimage/160/a8e5edf5f8fa4ac2800647b17fe74cbe.png",
      //       "UploadTime": "2024-02-26T18:07:16.198832+08:00",
      //       "ID": "506d7f62-b062-478c-b9bf-251d7f6f39ec",
      //       "Creator": null,
      //       "CreateTime": "0001-01-01T00:00:00",
      //       "LastModifier": null,
      //       "LastModifyTime": "0001-01-01T00:00:00"
      //     },
      //     content: 'http://resource.teld3.xyz/teldimage/160/a8e5edf5f8fa4ac2800647b17fe74cbe.png',
      //     message: '',
      //     status: 'done',
      //    }
      // ],
      draftContent: "", // 要提交的富文本内容
      draftWordLength: 0, // 纯文本字数，包含空格
      trimmedContent: "", // 草稿缩略显示用文字内容
      isFocused: false,
      isFullscreen: false,
    };
  },
  mounted() {
    this.bindEvents();
    setTimeout(() => {
      // 通过setTimeout确保上面绑定的focus事件会被执行
      // this.initFocus();
    });
  },
  beforeDestroy() {
    this.unbindEvents();
  },
  methods: {
    bindEvents() {
      const inputBox = this.$refs.draftEditorInputRef;
      if (inputBox) {
        inputBox.addEventListener("input", this.onInput);
      }
    },
    unbindEvents() {
      const inputBox = this.$refs.draftEditorInputRef;
      if (inputBox) {
        inputBox.addEventListener("input", this.onInput);
      }
    },
    initFocus() {
      const inputBox = this.$refs.draftEditorInputRef;
      inputBox.scrollTop = inputBox.scrollHeight;
      const range = new Range();
      range.selectNodeContents(inputBox);
      range.collapse(false);
      const selection = getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      setTimeout(() => {
        inputBox.focus();
      }, 300);
    },
    onInput(e) {
      console.log("onInput", e);
      this.draftContent = e.target.innerHTML;
      this.trimmedContent = e.target.textContent.trim();
      this.draftWordLength = e.target.innerText.length; // 含空格
    },
    onPaste(e) {
      e.preventDefault();
      const clipboardData = (e.originalEvent || e).clipboardData;
      if (!clipboardData) {
        return;
      }
      const pastedText = clipboardData.getData('text/plain');
      if (pastedText) {
        console.log('clipboardData text/plain', pastedText)
        document.execCommand('insertText', false, pastedText);
      }
    },
    onClickAt() {
      alert('onClickAt');
    },
    onClickAttachment() {
      alert('onClickAttachment')
    },
    /**
     * 点击发布评论/回复按钮的处理
     *
     * @description
     *  - 关于提交内容非空检查
     *    - 无附件时，不允许空提交，仅空格、仅回车、仅空格加回车都不行
     *    - 有附件时，允许空提交，仅空格、仅回车、仅空格加回车时，内容置换为空文字
     */
    onClickPublishComment() {
      if (this.isInputLoading) return;

      // 没有有效内容，且没有附件时
      if (!this.trimmedContent && !this.hasAttachments) {
        this.$TM.Toast('请检查输入内容');
        this.initFocus();
        return;
      }

      if (this.isInputOverflow) {
        this.$TM.Toast('内容超出文字数上限');
        this.initFocus();
        return;
      }

      if (this.draftContent.length > SUBMIT_CONTENT_LIMIT) {
        this.$TM.Toast(`内容超出可提交文字数上限(${this.draftContent.length}/${SUBMIT_CONTENT_LIMIT})`);
        this.initFocus();
        return;
      }

      this.isInputLoading = true;
      this.$emit('comment', {
        content: this.trimmedContent ? encodeURIComponent(this.draftContent) : '',
        fileList: this.fileList,
        // TODO:
        // userIdList
        onSucceed: (args = {}) => {
          this.reset(args);
        }
      });
    },
    reset({focus} = {}) {
      this.isInputLoading = false;
      // 清空内容
      const inputBox = this.$refs.draftEditorInputRef;
      if (inputBox) {
        inputBox.innerHTML = '';
      }
      this.draftContent = '';
      this.trimmedContent = '';
      this.draftWordLength = 0;
      this.fileList = [];
      // TODO PC端的tp-upload自定义上传需要手动清空文件，tm-uploader待确认
      // this.$refs.uploaderRef.clearFiles();

      // 主列表中添加评论后继续设回焦点
      if (focus) {
        setTimeout(() => {
          this.$refs.draftEditorInputRef.focus();
        }, 500)
      } else {
        this.isFocused = false;
      }
    },
    /**
     * -------------------------
     */
    showInputPopup() {
      this.isFocused = true;
      setTimeout(() => {
        this.initFocus();
      });
    },
    toggleFullscreen(e) {
      this.isFullscreen = !this.isFullscreen;
      this.initFocus();
    },
    onClickInputWrapper(e) {
      const inputBox = this.$refs.draftEditorInputRef;
      if (!inputBox.contains(e.target)) {
        this.initFocus();
      }
    },
    /**
     * -------------------------
     * 附件/文件上传相关
     */
    /**
     * tm-uploader读取文件内容前的处理，用于检查是否符合上传要求
     * 参数 file<File> 与 afterRead 里的不同
     */
    beforeRead(file, detail) {
      console.log('beforeRead', file, detail);
      // check file type
      const fileExt = file.name.split('.').pop();
      const checkFileType = SUPPORTED_FILE_TYPES.split(',').includes(fileExt);
      if (!checkFileType) {
        this.$TM.Toast(`不支持的文件类型(${file.name})`);
        return false;
      }

      // check file size
      const sizeBytes = this.getAttachmentMaxSize();
      if (file.size > sizeBytes) {
        const sizeFormat = formatBytes(sizeBytes);
        this.$TM.Toast(`请上传 ${sizeFormat} 以内的附件`);
        return false;
      }

      return true;
    },
    /**
     * tm-uploader组件读取文件内容后的处理
     *
     * file数据结构
     *  {
     *    content,
     *    file: <File> {
     *      lastModified,
     *      lastModifiedDate,
     *      name,
     *      size,
     *      type,
     *      webkitRelativePath,
     *    },
     *    message,
     *    status,
     *    result: Object<接口返回的结果>
     *  }
     */
    async afterRead(file, detail) {
      console.log('afterRead', file, detail);
      const onFail = (info) => {
        console.log(info);
        file.status = 'failed';
        this.$TM.Toast(`上传失败(${file.file.name})`);
        this.fileList.pop();
      }

      const base64String = file.content.split(',')[0];
      if (!base64String) {
        $TM.Toast(`文件内容为空(${file.file.name})`);
        return;
      }

      file.status = 'uploading';
      this.fileList.push(file);

      try {
        const { data } = await this.$utils.sgApi.getDataAsync({
          url: this.$utils.common.getServiceUrl('CMTS-UploadAttachment'),
          data: { param: JSON.stringify({
            FileName: file.file.name,
            FileContent: file.content,
          }) },
        });
        console.log('UploadAttachment', data);
        if (!data || data.state !== '1' ) {
          setTimeout(() => {
            onFail();
          }, 1500);
          return;
        }

        file.result = data.data;

        // file.result = {
        //   "CommentID": null,
        //   "AttachmentType": 2,
        //   "AttachmentUrl": "http://resource.teld3.xyz/teldimage/160/b3112f4f607f49efa4c2c6a253b00cd3.png",
        //   "UploadTime": "2024-02-27T14:56:15.0019732+08:00",
        //   "FileName": "附件上传的方式对比.docx",
        //   "ID": "16ead4d7-62dd-44fb-acfc-b8c8675f7010",
        //   "Creator": null,
        //   "CreateTime": "0001-01-01T00:00:00",
        //   "LastModifier": null,
        //   "LastModifyTime": "0001-01-01T00:00:00"
        // },
        setTimeout(() => {
          file.status = 'done';
        }, 500);
      } catch (error) {
        setTimeout(() => {
          onFail(error);
        }, 500);
      }
    },
    /**删掉添加的附件*/
    handleRemoveFile(index) {
      this.fileList.splice(index, 1);
    },
    /**点击附件图片进行大图查看*/
    onClickImage(e) {
      this.$TM.ImagePreview({images:[e.target.currentSrc]});
    },
    /**
     * 附件/文件上传相关
     * -------------------------
     */
  },
};
</script>

<style lang="scss" scoped></style>
